import { memo, useMemo } from 'react'
import { Node, Edge } from 'reactflow'
import { Label } from "@/shared/components/ui/label"
import { Volume2, Loader2 } from "lucide-react"
import { VoicePicker } from "@/modules/agent/components/VoicePicker"
import { useVoiceConfig } from "@/modules/agent/hooks/useVoiceConfig"

interface AgentVoiceConfigProps {
  selectedNode: Node
  edges: Edge[]
  outputNodes: Node[]
  onUpdateNodeData: (field: string, value: unknown) => void
}

export const AgentVoiceConfig = memo(({ 
  selectedNode, 
  edges, 
  outputNodes, 
  onUpdateNodeData 
}: AgentVoiceConfigProps) => {
  // 加载语音配置
  const { 
    ttsProviders, 
    defaultTtsProvider, 
    getVoicesByProvider,
    isLoading: isLoadingVoiceConfig 
  } = useVoiceConfig()
  
  // 判断后续节点是否为启用 TTS 的 Output 节点
  const nextOutputWithTTS = useMemo(() => {
    // 找到当前 Agent 节点的出边
    const outEdge = edges.find(e => e.source === selectedNode.id)
    if (!outEdge) return null
    
    // 找到目标节点
    const targetNode = outputNodes.find(n => n.id === outEdge.target)
    if (!targetNode) return null
    
    // 检查目标节点是否启用 TTS
    if (targetNode.data?.enableTTS) {
      return targetNode
    }
    
    return null
  }, [edges, outputNodes, selectedNode.id])
  
  // 如果没有启用 TTS 的后续 Output 节点，不显示
  if (!nextOutputWithTTS) {
    return null
  }
  
  // 获取后续 Output 节点的 TTS 提供商（用于显示对应的音色列表）
  const outputTtsProvider = nextOutputWithTTS.data?.ttsProvider || defaultTtsProvider || 'cosyvoice'
  const availableVoices = getVoicesByProvider(outputTtsProvider)
  
  return (
    <div className="pt-2 border-t mt-4">
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Volume2 className="h-4 w-4 text-green-500" />
          <Label className="text-sm font-medium">Agent 音色覆盖</Label>
        </div>
        <p className="text-xs text-slate-500">
          此 Agent 可使用不同于默认的音色进行语音回复
        </p>
        
        {isLoadingVoiceConfig ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            加载中...
          </div>
        ) : availableVoices.length > 0 ? (
          <div className="space-y-2">
            <VoicePicker
              value={selectedNode.data.voice_id}
              onValueChange={(value) => onUpdateNodeData('voice_id', value)}
              voices={availableVoices}
              showDefaultOption={true}
              defaultOptionText="使用 Output 节点默认音色"
              placeholder="选择音色"
            />
            <p className="text-xs text-slate-400">
              音色来自: {ttsProviders.find(p => p.id === outputTtsProvider)?.name || outputTtsProvider}
            </p>
          </div>
        ) : (
          <p className="text-xs text-amber-600">
            后续 Output 节点使用自定义 TTS，无法选择音色
          </p>
        )}
      </div>
    </div>
  )
})

AgentVoiceConfig.displayName = 'AgentVoiceConfig'